import AOS from 'aos';
import { createIcons, ArrowLeft, ArrowRightLeft, Blocks, Brain, Briefcase, Building2, Calendar, Check, ChevronDown, ChevronLeft, ChevronRight, Clock, Cloud, Cpu, Database, Eye, Facebook, FileText, Fingerprint, Folder, FolderOpen, FolderX, Globe, Github, Handshake, Headphones, Heart, Instagram, Lightbulb, Linkedin, Mail, MapPin, Menu, MessageCircle, Puzzle, Rocket, Search, ShieldCheck, ShoppingCart, Smile, Star, Tag, Target, TrendingUp, Twitter, User, UserCircle, Users, X, Zap } from 'lucide';

const usedIcons = { ArrowLeft, ArrowRightLeft, Blocks, Brain, Briefcase, Building2, Calendar, Check, ChevronDown, ChevronLeft, ChevronRight, Clock, Cloud, Cpu, Database, Eye, Facebook, FileText, Fingerprint, Folder, FolderOpen, FolderX, Globe, Github, Handshake, Headphones, Heart, Instagram, Lightbulb, Linkedin, Mail, MapPin, Menu, MessageCircle, Puzzle, Rocket, Search, ShieldCheck, ShoppingCart, Smile, Star, Tag, Target, TrendingUp, Twitter, User, UserCircle, Users, X, Zap };

const homeParticlesConfig = {
    fullScreen: { enable: false },
    particles: {
        number: { value: 50, density: { enable: true, area: 900 } },
        color: { value: ['#38bdf8', '#7dd3fc', '#ffffff'] },
        shape: { type: 'circle' },
        opacity: { value: { min: 0.15, max: 0.5 }, animation: { enable: true, speed: 0.8, sync: false } },
        size: { value: { min: 0.8, max: 3 }, animation: { enable: true, speed: 1.5, sync: false } },
        links: { enable: true, distance: 150, color: '#38bdf8', opacity: 0.18, width: 1 },
        move: {
            enable: true, speed: 1.2,
            direction: 'none', random: true,
            straight: false, outModes: 'out',
            attract: { enable: true, rotate: { x: 600, y: 1200 } }
        }
    },
    interactivity: {
        events: { onHover: { enable: false }, onClick: { enable: false }, resize: true }
    },
    detectRetina: true
};

const innerParticlesConfig = {
    fullScreen: { enable: false },
    particles: {
        number: { value: 20, density: { enable: true, area: 900 } },
        color: { value: ['#38bdf8', '#7dd3fc', '#ffffff'] },
        shape: { type: 'circle' },
        opacity: { value: { min: 0.1, max: 0.3 }, animation: { enable: true, speed: 0.5, sync: false } },
        size: { value: { min: 1, max: 2.5 } },
        links: { enable: true, distance: 150, color: '#38bdf8', opacity: 0.12, width: 1 },
        move: { enable: true, speed: 0.8, direction: 'none', random: true, straight: false, outModes: 'out' }
    },
    interactivity: { detectsOn: 'canvas', events: { onHover: { enable: false }, onClick: { enable: false }, resize: true } },
    detectRetina: true
};

document.addEventListener('DOMContentLoaded', async function() {
    // ---- Particles (tsParticles) — dynamically imported ----
    if (document.getElementById('particles-js') || document.getElementById('particles-inner')) {
        try {
            const [{ tsParticles }, { loadSlim }] = await Promise.all([
                import('tsparticles-engine'),
                import('tsparticles-slim'),
            ]);
            await loadSlim(tsParticles);
            if (document.getElementById('particles-js')) {
                await tsParticles.load('particles-js', homeParticlesConfig);
            }
            if (document.getElementById('particles-inner')) {
                await tsParticles.load('particles-inner', innerParticlesConfig);
            }
        } catch (e) {
            console.warn('Particles failed to load:', e);
        }
    }

    // ---- Lucide Icons ----
    createIcons({ icons: usedIcons });

    // ---- Navbar Scroll Effect ----
    const navbar = document.getElementById('navbar');
    function updateNavbar() {
        if (window.scrollY > 60) {
            navbar.classList.add('navbar-scrolled');
        } else {
            navbar.classList.remove('navbar-scrolled');
        }
    }
    window.addEventListener('scroll', updateNavbar, { passive: true });
    updateNavbar();

    // ---- AOS - Animate on Scroll ----
    AOS.init({ duration: 800, easing: 'ease-out-cubic', once: true, offset: 50 });

    // ---- Mobile Menu ----
    const menuToggle = document.getElementById('menu-toggle');
    const menuClose = document.getElementById('menu-close');
    const mobileMenu = document.getElementById('mobile-menu');
    const mobileOverlay = document.getElementById('mobile-overlay');
    const mobileLinks = document.querySelectorAll('.mobile-link');

    function openMenu() {
        mobileMenu.classList.add('open');
        mobileOverlay.classList.add('open');
        document.body.style.overflow = 'hidden';
    }
    function closeMenu() {
        mobileMenu.classList.remove('open');
        mobileOverlay.classList.remove('open');
        document.body.style.overflow = '';
    }

    if (menuToggle) menuToggle.addEventListener('click', openMenu);
    if (menuClose) menuClose.addEventListener('click', closeMenu);
    if (mobileOverlay) mobileOverlay.addEventListener('click', closeMenu);
    mobileLinks.forEach(link => link.addEventListener('click', closeMenu));

    // ---- Active Nav State ----
    var currentPage = document.body.getAttribute('data-page');
    if (currentPage) {
        document.querySelectorAll('[data-nav]').forEach(function(link) {
            if (link.getAttribute('data-nav') === currentPage) {
                link.classList.add('nav-active');
            }
        });
    }

    // ---- Testimonial Scroll Arrows ----
    var tScroll = document.getElementById('testimonial-scroll');
    var tPrev = document.getElementById('testimonial-prev');
    var tNext = document.getElementById('testimonial-next');
    if (tScroll && tPrev && tNext) {
        var scrollAmount = 400;
        tPrev.addEventListener('click', function() { tScroll.scrollBy({ left: -scrollAmount, behavior: 'smooth' }); });
        tNext.addEventListener('click', function() { tScroll.scrollBy({ left: scrollAmount, behavior: 'smooth' }); });
    }

    // ---- Language Dropdown ----
    var langTrigger = document.getElementById('lang-trigger');
    var langMenu = document.getElementById('lang-menu');
    if (langTrigger && langMenu) {
        langTrigger.addEventListener('click', function(e) {
            e.stopPropagation();
            var isOpen = langMenu.classList.contains('opacity-100');
            if (isOpen) {
                langMenu.classList.remove('opacity-100', 'visible', 'translate-y-0');
                langMenu.classList.add('opacity-0', 'invisible', '-translate-y-2');
                langTrigger.setAttribute('aria-expanded', 'false');
            } else {
                langMenu.classList.remove('opacity-0', 'invisible', '-translate-y-2');
                langMenu.classList.add('opacity-100', 'visible', 'translate-y-0');
                langTrigger.setAttribute('aria-expanded', 'true');
            }
        });
        document.addEventListener('click', function() {
            langMenu.classList.remove('opacity-100', 'visible', 'translate-y-0');
            langMenu.classList.add('opacity-0', 'invisible', '-translate-y-2');
            if (langTrigger) langTrigger.setAttribute('aria-expanded', 'false');
        });
    }

    // ---- Contact Form AJAX ----
    var contactForm = document.getElementById('contact-form');
    if (contactForm) {
        contactForm.addEventListener('submit', function(e) {
            e.preventDefault();
            var form = this;
            var submitBtn = form.querySelector('button[type="submit"]');
            var originalText = submitBtn.textContent;
            submitBtn.disabled = true;
            submitBtn.textContent = '...';

            fetch(form.action, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                },
                body: new FormData(form),
            })
            .then(function(res) { return res.json().then(function(data) { return { ok: res.ok, data: data }; }); })
            .then(function(result) {
                if (result.ok) {
                    showToast(result.data.message, 'success');
                    form.reset();
                    if (window.turnstile) turnstile.reset();
                } else {
                    if (result.data.errors) {
                        var firstError = Object.values(result.data.errors)[0][0];
                        showToast(firstError, 'error');
                    } else {
                        showToast(result.data.message || 'Something went wrong.', 'error');
                    }
                }
            })
            .catch(function() {
                showToast('Something went wrong. Please try again.', 'error');
            })
            .finally(function() {
                submitBtn.disabled = false;
                submitBtn.textContent = originalText;
            });
        });
    }

});

// showToast needs to be global for inline usage
window.showToast = function(msg, type) {
    var existing = document.querySelector('.toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'toast toast-' + (type || 'success');
    toast.textContent = msg;
    document.body.appendChild(toast);
    requestAnimationFrame(function() { toast.classList.add('show'); });
    setTimeout(function() { toast.classList.remove('show'); setTimeout(function() { toast.remove(); }, 400); }, 4000);
};
